// Central server clock — the whole platform reads booking / calendar
// "now" from here instead of `new Date()`. Requested Feb 2026 so a
// yacht laptop with a drifted clock cannot silently poison scheduling.
//
// Contract
// --------
//  - `useServerClock()`  → { serverNow, driftMs, yachtTz, loading }
//  - `getServerNow()`    → Date reflecting the LAST synced server clock
//                          + the elapsed real time since that sync.
//                          Safe to call in event handlers.
//
// The offset is re-synced on: mount, tab-focus, and every 60 seconds.
// A single request in-flight is deduped so multiple mounts don't storm
// the endpoint.

import { useEffect, useState, useCallback } from "react";
import { api } from "./api";

let _offsetMs = 0;           // server_ms - performance.now-anchor
let _anchorPerf = 0;         // performance.now() at last sync
let _yachtTz = null;
let _lastSyncAt = 0;
let _inflight = null;
const _listeners = new Set();

async function _sync() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const t0 = performance.now();
      const { data } = await api.get("/public/server-time");
      const t1 = performance.now();
      // Compensate for network latency by treating the midpoint of the
      // round-trip as when the server's clock had the value it reported.
      const halfRtt = (t1 - t0) / 2;
      const serverMs = Number(data?.server_ms) || Date.now();
      const anchoredServerMs = serverMs + halfRtt;
      _offsetMs = anchoredServerMs - t1;
      _anchorPerf = t1;
      _yachtTz = data?.yacht_tz || _yachtTz || "UTC";
      _lastSyncAt = Date.now();
      _listeners.forEach((fn) => { try { fn(); } catch {} });
      return true;
    } catch {
      return false;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/** Millisecond-precision server "now" — never trusts the device clock. */
export function getServerNowMs() {
  // Fallback to Date.now() before the first sync so consumers don't crash.
  if (!_anchorPerf) return Date.now();
  return _offsetMs + performance.now();
}

/** Convenience — Date wrapper. */
export function getServerNow() {
  return new Date(getServerNowMs());
}

/** Difference between the device clock and the server (device - server, ms). */
export function getDeviceDriftMs() {
  if (!_lastSyncAt) return 0;
  // Device thinks "now" is Date.now(); server thinks it's getServerNowMs().
  return Date.now() - getServerNowMs();
}

/** React hook — schedules a re-render every second so time-driven UI ticks. */
export function useServerClock() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const bump = () => { if (!cancelled) setTick((n) => n + 1); };
    _listeners.add(bump);
    if (!_anchorPerf) _sync();
    // Re-sync every 60 s + on tab focus.
    const iv = setInterval(_sync, 60_000);
    const onFocus = () => _sync();
    const onVis = () => { if (document.visibilityState === "visible") _sync(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    // Tick every 1 s so live-updating labels re-render.
    const rerender = setInterval(bump, 1000);
    return () => {
      cancelled = true;
      _listeners.delete(bump);
      clearInterval(iv);
      clearInterval(rerender);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return {
    // Every render, expose a fresh Date so components using it stay in sync.
    serverNow: getServerNow(),
    serverNowMs: getServerNowMs(),
    driftMs: getDeviceDriftMs(),
    yachtTz: _yachtTz || "UTC",
    loading: !_anchorPerf,
    tick, // exposed so parent memos invalidate on our tick
  };
}
